import React, { useEffect, useState } from 'react'
import dayjs from 'dayjs';
import {
    Table,
    Box,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Typography,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Divider,
    Chip,
    Tooltip
} from "@mui/material";
import { toast } from 'react-toastify';
import { Pagination } from '@mui/material';
import { get_gio_buying_list, gioo_buying_cancel_request, get_gio_buying_detail, get_syndicate_buying_list, get_syndicate_buying_detail, syndicate_buying_cancel_request, get_purchase_bank_details, get_all_bank_details } from '../../../api/ControllApi';
import { useSearchParams, useNavigate } from "react-router-dom";
import TransactionDetailsModal from '../../../components/common/TransactionDetailsModal';



const PurchaseList = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [openBankWarningDialog, setOpenBankWarningDialog] = useState(false);
    const [pendingCancelId, setPendingCancelId] = useState(null);
    const [selectedId, setSelectedId] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);

    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);

    const [detailsPage, setDetailsPage] = useState(1);
    const [detailsRowsPerPage, setDetailsRowsPerPage] = useState(10);
    const [totalDetailsRows, setTotalDetailsRows] = useState(0);

    const [openModal, setOpenModal] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState([]);
    const [bookingDetails, setBookingDetails] = useState(null); // NEW

    const [buyingList, setBuyingList] = useState([]);
    const [tabIndex, setTabIndex] = useState(0);
    const [searchParams, setSearchParams] = useSearchParams();

    const [openTransactionModal, setOpenTransactionModal] = useState(false);
    const [selectedSyndicatePropertyId, setSelectedSyndicatePropertyId] = useState(null);
    const [selectedTransactionId, setSelectedTransactionId] = useState(null);
    const [isPreviewMode, setIsPreviewMode] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [allBankDetails, setAllBankDetails] = useState([]);

    // Expiry threshold for Gio Nano Plots (1 year)
    const EXPIRY_HOURS = 8760;

    // ... (rest of the component logic up to table rendering)
    const isExpired = (createdAt) => {
        if (!createdAt) return false;
        const now = dayjs();
        const created = dayjs(createdAt);
        return now.diff(created, 'hour') >= EXPIRY_HOURS;
    };


    useEffect(() => {
        const tab = searchParams.get("tab");
        if (tab === "syndicate") {
            setTabIndex(1);
        } else {
            setTabIndex(0);
        }
    }, []);


    const handleOpenModal = (transaction) => {
        setSelectedTransaction(transaction);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setSelectedTransaction([]);
        setBookingDetails(null); // RESET
        setOpenModal(false);
    };

    const checkHasBankDetails = async () => {
        const userPhone = localStorage.getItem("phone") || "guest";
        const storageKey = `cashback_bank_details_${userPhone}`;

        // Always fetch fresh from API first for accuracy
        try {
            const res = await get_all_bank_details();
            if (res?.status && res?.data) {
                const apiDataList = Array.isArray(res.data) ? res.data : [res.data];
                // Only count active bank accounts
                const activeAccounts = apiDataList.filter(
                    (acc) => String(acc.status).toLowerCase() === "active"
                );
                if (activeAccounts.length > 0) {
                    return true;
                }
            }
        } catch (err) {
            console.error("Failed to check bank details from API:", err);
        }

        // Fallback to localStorage if API fails
        try {
            const stored = localStorage.getItem(storageKey);
            if (stored) {
                const parsed = JSON.parse(stored);
                if (Array.isArray(parsed)) {
                    const activeAccounts = parsed.filter(
                        (acc) => String(acc.status).toLowerCase() === "active"
                    );
                    if (activeAccounts.length > 0) {
                        return true;
                    }
                }
            }
        } catch (e) {
            console.error(e);
        }

        return false;
    };

    const handleOpenDialog = async (id) => {
        setLoading(true);
        const hasBank = await checkHasBankDetails();
        setLoading(false);
        if (!hasBank) {
            // No active bank details — close modal and redirect to account details
            setOpenModal(false);
            toast.info("Please add an active bank account before cancelling a purchase.");
            navigate("/dashboard/account-details");
            return;
        }
        setSelectedId(id);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedId(null);
    };

    const handleTabChange = (index) => {
        setTabIndex(index);
        setPage(1);
        setSearchParams({
            tab: index === 1 ? "syndicate" : "gioo"
        });
    };


    const handleConfirmCancel = async (id) => {
        handleCloseDialog();
        try {
            const res =
                tabIndex === 0
                    ? await gioo_buying_cancel_request(id)
                    : await syndicate_buying_cancel_request(id);

            toast.success(res?.message || "Cancelled successfully");
            // Stay on the same page — refresh the list and re-open the modal with updated data
            setOpenModal(false);
            await fetchBuyingList(tabIndex, page);
        } catch (err) {
            const errorMessage = err.response?.data?.message;
            let message = "Failed to cancel purchase";

            // Check for KYC-related error or syndicate-specific error
            if (errorMessage?.toLowerCase().includes('kyc') ||
                (tabIndex === 1 && errorMessage?.toLowerCase().includes('verified'))) {
                message = "KYC is already verified, so this syndicate cannot be cancelled";
            } else if (errorMessage) {
                message = errorMessage;
            }

            toast.error(message);
            handleCloseDialog();
        }
    };

    const fetchBuyingList = async (tab, page = 1, perPage = 10) => {
        setLoading(true);
        try {
            let res;
            if (tab === 0) {
                res = await get_gio_buying_list(page);
            } else {
                res = await get_syndicate_buying_list(page);
            }
            const initialData = res?.data || [];
            
            // Fetch all bank details
            let bankDetails = allBankDetails;
            if (bankDetails.length === 0) {
                try {
                    const bankRes = await get_all_bank_details();
                    if (bankRes.success && Array.isArray(bankRes.data)) {
                        bankDetails = bankRes.data;
                        setAllBankDetails(bankRes.data);
                    }
                } catch (e) {
                    console.error("Error fetching all bank details:", e);
                }
            }

            // Map and mark items
            const updatedData = initialData.map((item) => {
                const propId = item.property_id || item.id;
                const type = tab === 0 ? "gioo" : "syndicate";
                
                const isAdded = bankDetails.some(bd => 
                    (String(bd.property_id) === String(propId)) && 
                    (String(bd.type) === String(type))
                );
                
                return { ...item, is_purchase_bank_details: isAdded || item.is_purchase_bank_details };
            });

            setBuyingList(updatedData);
            setTotalRows(res.pagination?.total || res.data.length || 0);
        } catch (err) {
            console.error("Error fetching buying list:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
        fetchBuyingList(tabIndex, newPage, rowsPerPage);
    };

    const handleChangeRowsPerPage = (event) => {
        const newRows = parseInt(event.target.value, 10);
        setRowsPerPage(newRows);
        setPage(1);
        fetchBuyingList(tabIndex, 1, newRows);
    };

    const handleViewBankDetails = async (propertyId, type) => {
        setLoading(true);
        try {
            const res = await get_purchase_bank_details(propertyId, type);
            if (res.status === true || res.success === true || res.data) {
                setPreviewData(res.data);
                setIsPreviewMode(true);
                setSelectedSyndicatePropertyId(propertyId);
                setOpenTransactionModal(true);
            } else {
                toast.error(res.message || "Failed to fetch bank details");
            }
        } catch (err) {
            toast.error("Error fetching bank details");
        } finally {
            setLoading(false);
        }
    };

    const handleViewDetail = async (id, page = 1) => {
        setLoadingDetails(true);
        try {
            let res =
                tabIndex === 0
                    ? await get_gio_buying_detail(id, page)
                    : await get_syndicate_buying_detail(id, page);

            if (res?.status) {
                setSelectedTransaction(res.data);
                setBookingDetails(res.booking || null); // STORE booking
                setTotalDetailsRows(res.pagination?.total || res.data.length || 0);
                setDetailsPage(page);
                setOpenModal(true);
            } else {
                toast.error(res?.message || "Failed to fetch details");
            }
        } catch (err) {

            toast.error("Something went wrong while fetching details");
        } finally {
            setLoadingDetails(false);
        }
    };

    useEffect(() => {
        fetchBuyingList(tabIndex);
    }, [tabIndex]);


    // Helper to calculate total from table rows
    const tableTotal = selectedTransaction.reduce((sum, item) => {
        return sum + parseFloat(item.amount || item.transaction?.amount || 0);
    }, 0);


    return (
        <div>
            <Typography variant="h5" fontWeight={500} mb={{ xs: 0, sm: 0 }}>
                Purchase List
            </Typography>

            {/* Tabs */}
            <Box sx={{ display: "flex", gap: 2, mb: 3, mt: 3 }}>
                {["Gio Nano Plots", "Gio Rental Yield – Syndicate Plot"].map((label, index) => (
                    <Box
                        key={index}
                        onClick={() => handleTabChange(index)}
                        sx={{
                            px: 3,
                            py: 1,
                            borderRadius: 2,
                            cursor: "pointer",
                            fontWeight: tabIndex === index ? 600 : 400,
                            backgroundColor: tabIndex === index ? "primary.main" : "#f0f0f0",
                            color: tabIndex === index ? "#fff" : "#000",
                            boxShadow: tabIndex === index ? 3 : 1,
                            transition: "all 0.2s",
                            "&:hover": {
                                backgroundColor: tabIndex === index ? "primary.dark" : "#e0e0e0",
                            },
                        }}
                    >
                        {label}
                    </Box>
                ))}
            </Box>

            {/* Main Table */}
            <TableContainer component={Paper} sx={{ mt: 4 }}>
                <Table>                    <TableHead>
                    <TableRow sx={{ backgroundColor: "primary.light", color: "white" }}>
                        <TableCell>Property Name</TableCell>
                        <TableCell>Unit</TableCell>
                        {tabIndex !== 0 && (
                            <>
                                <TableCell>Amount</TableCell>
                                <TableCell>Transaction ID</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Tracking</TableCell>
                            </>
                        )}
                        <TableCell>Action</TableCell>
                    </TableRow>
                </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={tabIndex === 0 ? 3 : 7} align="center">Loading...</TableCell>
                            </TableRow>
                        ) : buyingList.length > 0 ? (
                            buyingList.map((item) => (
                                <TableRow key={item.transaction_id || item.id}>
                                    <TableCell>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Typography variant="body2">{item?.property?.name || "Not Found"}</Typography>
                                            {item.sold_status === 1 && (
                                                <Tooltip title="Your funds will be reimbursed shortly." arrow placement="top">
                                                    <Chip label="Sold Out" color="error" size="small" />
                                                </Tooltip>
                                            )}

                                            {tabIndex === 0 && item.sold_status === 0 && isExpired(item?.transaction?.created_at) && (
                                                <Chip label="Expired" color="warning" size="small" />
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell>{item?.units ? item.units.split(",").length : 0}</TableCell>
                                    {tabIndex !== 0 && (
                                        <>
                                            <TableCell>{item?.refund_amount && Number(item.refund_amount) > 0 ? item.refund_amount : (item?.final_amount || "-")}</TableCell>
                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontFamily: 'monospace',
                                                        fontSize: '11px',
                                                        color: item.transaction_user_id ? 'text.primary' : 'text.disabled',
                                                        wordBreak: 'break-all',
                                                        maxWidth: 140,
                                                    }}
                                                >
                                                    {item.transaction_user_id || "—"}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    onClick={() => {
                                                        const type = tabIndex === 0 ? "gioo" : "syndicate";
                                                        if (item.is_purchase_bank_details) {
                                                            handleViewBankDetails(item.property_id || item.id, type);
                                                        } else {
                                                            setIsPreviewMode(false);
                                                            setPreviewData(null);
                                                            setSelectedSyndicatePropertyId(item.property_id);
                                                            setSelectedTransactionId(item.transaction_id);
                                                            setOpenTransactionModal(true);
                                                        }
                                                    }}
                                                    sx={{
                                                        textTransform: "none",
                                                        backgroundColor: "primary.main",
                                                        borderRadius: "50px",
                                                        fontSize: "11px",
                                                    }}
                                                >
                                                    {item.is_purchase_bank_details ? "Bank Details Added" : "Add Transaction Details"}
                                                </Button>
                                            </TableCell>
                                            <TableCell>
                                                {(!item.dtdc_id && !item.tracking_id) ? (
                                                    <Typography variant="body2" color="text.secondary">
                                                        Processing
                                                    </Typography>
                                                ) : (
                                                    <Typography variant="body2">
                                                        {item.dtdc_id || item.tracking_id}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                        </>
                                    )}
                                    <TableCell>
                                        <Button
                                            variant="outlined"
                                            color="secondary"
                                            onClick={() => handleViewDetail(item.transaction_id)}
                                        >
                                            View
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={tabIndex === 0 ? 3 : 7} align="center">No data found</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination */}
            {!loading && totalRows > rowsPerPage && (
                <Box display="flex" justifyContent="center" mt={2}>
                    <Pagination
                        count={Math.ceil(totalRows / rowsPerPage)}
                        page={page}
                        onChange={handleChangePage}
                        color="primary"
                    />
                </Box>
            )}

            {/* Cancel Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog}>
                <DialogTitle>Cancel Purchase</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to cancel this purchase? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} color="primary">No</Button>
                    <Button onClick={() => handleConfirmCancel(selectedId)} color="error" variant="contained">
                        Yes, Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Bank Details Required Warning Dialog */}
            <Dialog
                open={openBankWarningDialog}
                onClose={() => setOpenBankWarningDialog(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'warning.dark' }}>
                    🏦 Bank Details Required
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        To cancel a purchase and receive your refund, you need to have at least one <strong>active bank account</strong> on file.
                        <br /><br />
                        Please add your bank details in <strong>Account Details</strong> and then try cancelling again.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button
                        onClick={() => setOpenBankWarningDialog(false)}
                        color="inherit"
                        variant="outlined"
                    >
                        Close
                    </Button>
                    <Button
                        onClick={() => {
                            setOpenBankWarningDialog(false);
                            navigate("/dashboard/account-details");
                        }}
                        color="primary"
                        variant="contained"
                        sx={{ textTransform: 'none' }}
                    >
                        Go to Account Details
                    </Button>
                </DialogActions>
            </Dialog>



            {/* Modal for Transaction Details */}
            <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Transaction Details
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseModal}
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        ✕
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers>
                    {loadingDetails ? (
                        <Typography align="center">Loading...</Typography>
                    ) : selectedTransaction.length > 0 ? (
                        <>
                            {/* Units Table */}
                            <TableContainer component={Paper} sx={{ mt: 2 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: "primary.light", color: "white" }}>
                                            <TableCell>Unit</TableCell>
                                            <TableCell>Amount</TableCell>
                                            <TableCell>Action</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {selectedTransaction.map((unitItem) => (
                                            <TableRow key={unitItem.id}>
                                                <TableCell>
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <Typography variant="body2">{unitItem.unit}</Typography>
                                                        {unitItem.sold_status === 1 && (
                                                            <Tooltip title="Your funds will be reimbursed shortly." arrow placement="top">
                                                                <Chip label="Sold Out" color="error" size="small" />
                                                            </Tooltip>
                                                        )}

                                                        {tabIndex === 0 && unitItem.sold_status === 0 && isExpired(unitItem?.transaction?.created_at) && (
                                                            <Chip label="Expired" color="warning" size="small" />
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    {unitItem?.refund_amount && Number(unitItem.refund_amount) > 0 ? unitItem.refund_amount : (unitItem.amount || "—")}
                                                </TableCell>
                                                <TableCell>
                                                    {unitItem.sold_status === 1 ? (
                                                        <Typography variant="caption" color="text.secondary">
                                                            Pending Reimbursement
                                                        </Typography>
                                                    ) : (
                                                        <>
                                                            {unitItem.cancel_status === 0 && (
                                                                <Button variant="outlined" color="error" onClick={() => handleOpenDialog(unitItem?.id)}>
                                                                    Cancel Purchase
                                                                </Button>
                                                            )}
                                                            {unitItem.cancel_status === 1 && (
                                                                <Button variant="outlined" color="secondary">Request Sent</Button>
                                                            )}
                                                            {unitItem.cancel_status === 2 && unitItem.refund_status === 0 && (
                                                                <>
                                                                    <Button variant="outlined" color="inherit" disabled>Cancelled</Button>
                                                                    <Typography variant="caption" display="block"> (Refund will be sent in 2 Days)</Typography>
                                                                </>
                                                            )}
                                                            {unitItem.cancel_status === 3 && unitItem.refund_status === 0 && (
                                                                <>
                                                                    <Button variant="outlined" color="inherit" disabled>Declined</Button>
                                                                    <Typography variant="caption" display="block"> (Your cancel request is declined)</Typography>
                                                                </>
                                                            )}
                                                            {unitItem.refund_status === 1 && (
                                                                <>
                                                                    <Button variant="outlined" color="inherit" disabled>
                                                                        Refunded Successfully
                                                                    </Button>
                                                                    {unitItem.user_transaction_id && (
                                                                        <Typography variant="caption" display="block">
                                                                            Transaction ID: {unitItem.user_transaction_id}
                                                                        </Typography>
                                                                    )}
                                                                </>
                                                            )}
                                                        </>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}

                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* Details Pagination */}
                            {totalDetailsRows > detailsRowsPerPage && (
                                <Box display="flex" justifyContent="center" mt={2}>
                                    <Pagination
                                        count={Math.ceil(totalDetailsRows / detailsRowsPerPage)}
                                        page={detailsPage}
                                        onChange={(event, newPage) =>
                                            handleViewDetail(selectedTransaction[0].transaction_id, newPage)
                                        }
                                        color="primary"
                                    />
                                </Box>
                            )}
                        </>
                    ) : (
                        <Typography align="center">No units found</Typography>
                    )}
                </DialogContent>
            </Dialog>

            <TransactionDetailsModal
                open={openTransactionModal}
                onClose={() => {
                    setOpenTransactionModal(false);
                    setIsPreviewMode(false);
                    setPreviewData(null);
                }}
                propertyId={selectedSyndicatePropertyId}
                transactionId={selectedTransactionId}
                type={tabIndex === 0 ? "gioo" : "syndicate"}
                onSuccess={() => fetchBuyingList(tabIndex, page)}
                isPreview={isPreviewMode}
                data={previewData}
            />
        </div>
    );
};

export default PurchaseList;